import { FulfillmentState, GuaranteeDecision, GuaranteeProcessingState, PartnerServiceType, PrescriptionLineView, ServiceKind } from '@lib/guarantees/data-access';

/** Arranged and not yet settled either way. A rescheduled slot is as live as a plainly booked one. */
const OPEN_FULFILLMENT_STATES: ReadonlySet<FulfillmentState> = new Set([FulfillmentState.Planned, FulfillmentState.Rescheduled, FulfillmentState.InProgress]);

/**
 * The booking a line is still waiting on, which is the one an outcome is recorded against.
 *
 * Only the last attempt is asked — the same one the line's state is read off, so the chip and the
 * menu can never disagree. Picking the last *open* attempt instead would answer with a slot that a
 * reschedule vacated, since the entry it moved away from stays as it was written.
 */
export function openPlanningId(line: PrescriptionLineView): string | undefined {
  return OPEN_FULFILLMENT_STATES.has(line.fulfillment) ? line.planning?.id : undefined;
}

/**
 * Whether there is still delivery to arrange. Care already given is not arranged again, and a
 * booking still standing would be double-booked — ending the one that is open is what opens the way
 * back to this.
 */
export function allowsExecution(line: PrescriptionLineView): boolean {
  return !line.prescription.isDeleted && line.fulfillment !== FulfillmentState.Fulfilled && !openPlanningId(line);
}

/**
 * Which kind of partner delivers this service — the capability the directory is searched by.
 *
 * Held once because two screens ask it of the same line: the one that narrows where a line may be
 * delivered, and the one that arranges the delivery. Two copies would drift.
 */
const PARTNER_SERVICE: Record<ServiceKind, PartnerServiceType> = {
  [ServiceKind.Appointment]: PartnerServiceType.Appointment,
  [ServiceKind.PersonalDoctorAppointment]: PartnerServiceType.Appointment,
  [ServiceKind.InstrumentalDiagnostic]: PartnerServiceType.Appointment,
  [ServiceKind.LaboratoryTest]: PartnerServiceType.Laboratory,
  [ServiceKind.Medication]: PartnerServiceType.Pharmacy,
  [ServiceKind.Other]: PartnerServiceType.Appointment,
  [ServiceKind.Unknown]: PartnerServiceType.Appointment,
};

export function partnerServiceFor(kind: ServiceKind): PartnerServiceType {
  return PARTNER_SERVICE[kind] ?? PartnerServiceType.Appointment;
}

/**
 * Why delivery cannot be arranged for this line, whichever way it is arranged.
 *
 * Both are the same fact from two sides: every arrangement needs the member's outstanding task for
 * the line, and those are raised only once the record has been processed and the line granted
 * cover. Until then there is nothing for the arrangement to hang off.
 */
export enum BookingBlocker {
  /** The record's review is still open, so no task has been raised off it yet. */
  NotProcessed = 'NotProcessed',
  /** No cover granted, so no task was raised for this line even though the record was processed. */
  NotGuaranteed = 'NotGuaranteed',
}

export const BOOKING_BLOCKER_HINT_KEY: Record<BookingBlocker, string> = {
  [BookingBlocker.NotProcessed]: 'guaranteeV2.message.bookingNotProcessed',
  [BookingBlocker.NotGuaranteed]: 'guaranteeV2.message.bookingNotGuaranteed',
};

/**
 * A free-form line is the exception throughout: it is never shown to the member, so it never had a
 * task, and booking it is allowed without one.
 */
export function bookingBlocker(line: PrescriptionLineView): BookingBlocker | undefined {
  if (line.prescription.isFreeForm) {
    return undefined;
  }

  if (line.guaranteeStatus !== GuaranteeProcessingState.Processed) {
    return BookingBlocker.NotProcessed;
  }

  return line.prescription.decision?.decision === GuaranteeDecision.Guaranteed ? undefined : BookingBlocker.NotGuaranteed;
}

/**
 * Offered only where it changes something: with a contract in force there is cover to chase for
 * delivery, and saying the client settled it themselves would contradict that.
 */
export function allowsConsiderPaid(line: PrescriptionLineView): boolean {
  return !line.prescription.isDeleted && !line.contractInForce && !line.prescription.financials?.considerPaid;
}

/**
 * Whether the line can still be settled as delivered on a visit already held. Only where nothing
 * else answers it: a line already delivered or with a booking still standing is settled through
 * that booking, and a drug is collected at a pharmacy rather than given on a visit.
 */
export function allowsCompletionInAppointment(line: PrescriptionLineView): boolean {
  return allowsExecution(line) && !line.prescription.completedInAppointment && line.prescription.kind !== ServiceKind.Medication;
}

/** An outcome is recorded against a booking, so there has to be one still open to record it on. */
export function allowsEndFulfillment(line: PrescriptionLineView): boolean {
  return !line.prescription.isDeleted && !!openPlanningId(line);
}

/** Members of a course with a live booking — the ones a course-wide stop actually ends. */
export function stoppableLines(lines: PrescriptionLineView[]): PrescriptionLineView[] {
  return lines.filter(line => allowsEndFulfillment(line));
}

/**
 * The other lines one arrangement could also deliver: the same kind of service on the same record,
 * with nothing currently booked against them.
 *
 * Only an order has any: a laboratory or a pharmacy fills one order with several items, whereas a
 * visit delivers one service — a consultation or a diagnostic is booked on its own, so a line
 * delivered at a visit offers no siblings at all. Within an order it reaches across the whole
 * guarantee rather than the diagnosis group: sessions of one course are routinely filed under
 * different diagnoses, and the partner delivers them in one order regardless.
 */
export function bookableSiblings(lines: PrescriptionLineView[], line: PrescriptionLineView): PrescriptionLineView[] {
  if (partnerServiceFor(line.prescription.kind) === PartnerServiceType.Appointment) {
    return [];
  }

  return lines.filter(candidate => candidate.prescription.id !== line.prescription.id && candidate.prescription.kind === line.prescription.kind && allowsExecution(candidate));
}
